import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { EntityForm } from "../components/EntityForm";
import { CommentsAudit } from "../components/CommentsAudit";
import { AccountNetwork, AccountKpis } from "../components/AccountExtras";
import { ForecastPanel } from "../components/ForecastPanel";
import { GroupRollup } from "../components/GroupRollup";
import { VisitNotes } from "../components/VisitNotes";
import { WalletShare } from "../components/WalletShare";
import { AccountAnalysis } from "../components/AccountAnalysis";
import { OrgChart } from "../components/OrgChart";
import { AccountOrders, AccountContracts, AccountConditions, AccountArticles, AccountSamplesArtworks, AccountActivities, AccountLocations } from "../components/AccountRelated";
import { ContractEditDialog } from "./Contracts";
import { ConditionEditDialog, ArticleEditDialog } from "../components/RecordEditDialogs";
import { OpportunityDetailDialog } from "../components/OpportunityDetailDialog";
import { QuoteDetailDialog } from "../components/QuoteDetailView";
import { STAGE_COLORS, SEVERITY_COLORS, QUOTE_STATUS_COLORS } from "../lib/fields";
import { ArrowLeft, Building2, Pencil, GitBranch, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

function Kpi({ label, value, accent }) {
  return (
    <div className="text-center px-4">
      <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">{label}</p>
      <p className={`text-lg font-bold font-mono ${accent ? "text-[#F5A623]" : "text-white"}`}>{value}</p>
    </div>
  );
}

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, canWrite, hasIntel, canReassign, plLabel, user, perms } = useApp();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newContact, setNewContact] = useState(false);
  const [edit, setEdit] = useState(null);
  const [oppDetail, setOppDetail] = useState(null);
  const [quoteDetail, setQuoteDetail] = useState(null);
  const [relatedToken, setRelatedToken] = useState(0);
  const [users, setUsers] = useState([]);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [ownerDraft, setOwnerDraft] = useState([]);
  const [history, setHistory] = useState([]);

  const load = () => api.get(`/accounts/${id}/summary`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (canReassign) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [canReassign]);

  const nameOf = (uid) => (users.find((u) => u.id === uid) || {}).name || uid;
  const openOwners = () => {
    setOwnerDraft(data?.account?.owner_ids || (data?.account?.owner_id ? [data.account.owner_id] : []));
    api.get(`/accounts/${id}/assignment-history`).then((r) => setHistory(r.data)).catch(() => {});
    setOwnersOpen(true);
  };
  const toggleOwner = (uid) => setOwnerDraft((d) => d.includes(uid) ? d.filter((x) => x !== uid) : [...d, uid]);
  const saveOwners = async () => {
    try { await api.put(`/accounts/${id}/owners`, { owner_ids: ownerDraft }); toast.success(t("save") + " ✓"); setOwnersOpen(false); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const closeEdit = (changed) => { setEdit(null); if (changed) { setRelatedToken((x) => x + 1); load(); } };
  const openContactEdit = (cid) =>
    api.get(`/contacts/${cid}`).then((r) => setEdit({ kind: "contacts", data: r.data })).catch((e) => toast.error(errDetail(e)));

  if (!data) return <p className="text-gray-400">{t("loading")}</p>;

  if (data.restricted) {
    const a = data.account;
    const segs = (Array.isArray(a.segments) && a.segments.length ? a.segments : (a.segment ? [a.segment] : []));
    return (
      <div className="space-y-5" data-testid="account-detail-restricted">
        <button onClick={() => navigate("/accounts")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="back-to-accounts">
          <ArrowLeft className="h-4 w-4" />{t("accounts")}
        </button>
        <div className="bg-[#1B2A4A] rounded-lg p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-[#F5A623] flex items-center justify-center shrink-0"><Building2 className="h-6 w-6 text-[#1B2A4A]" /></div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }} data-testid="account-name">{a.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-white/60">
                <span className="font-mono">{a.account_number}</span>·<span>{a.type}</span>·<span>{segs.join(", ") || "–"}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 bg-amber-500/15 border border-amber-400/30 text-amber-200 rounded-md px-3 py-2 text-sm" data-testid="restricted-banner">
            <ShieldCheck className="h-4 w-4" />{t("restricted_hint")}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          {[["city", a.city], ["billing_address", a.billing_address], ["region", a.region], ["vat_id", a.vat_id], ["status", a.status]].map(([k, v]) => (
            <div key={k}><p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{t(k)}</p><p>{v || "–"}</p></div>
          ))}
        </div>
      </div>
    );
  }

  const { account: a, kpis, contacts, opportunities, quotes, complaints, children } = data;
  const isGroup = a.type === "Global Customer" || (children && children.length > 0);
  const ownerIds = a.owner_ids && a.owner_ids.length ? a.owner_ids : (a.owner_id ? [a.owner_id] : []);
  const kv = (v) => (v == null ? "—" : v);

  return (
    <div className="space-y-5" data-testid="account-detail-page">
      <button onClick={() => navigate("/accounts")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors duration-150" data-testid="back-to-accounts">
        <ArrowLeft className="h-4 w-4" />{t("accounts")}
      </button>
      <div className="bg-[#1B2A4A] rounded-lg p-6 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-[#F5A623] flex items-center justify-center shrink-0">
              <Building2 className="h-6 w-6 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }} data-testid="account-name">{a.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-white/60">
                <span className="font-mono">{a.account_number}</span>·<span>{a.type}</span>·<span data-testid="account-detail-segments">{(Array.isArray(a.segments) && a.segments.length ? a.segments : (a.segment ? [a.segment] : [])).join(", ") || "–"}</span>
                <Badge className="bg-white/10 text-white hover:bg-white/10">{a.abc_class}</Badge>
                <Badge className={a.status === "Active" ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20" : "bg-amber-500/20 text-amber-300 hover:bg-amber-500/20"}>{a.status}</Badge>
                {(a.tags || []).map((tg) => <Badge key={tg} className="bg-white/10 text-white/70 hover:bg-white/10">{tg}</Badge>)}
                {(a.roles || []).includes("supplier") && (
                  <Badge className="bg-[#F5A623]/20 text-[#F5A623] hover:bg-[#F5A623]/20 cursor-pointer" data-testid="account-supplier-badge" onClick={() => navigate(`/suppliers/${id}`)}>Lieferant</Badge>
                )}
                {["srm_admin", "category_manager"].includes(perms?.role_key) || user?.role === "admin" ? (
                  !(a.roles || []).includes("supplier") && (
                    <button data-testid="account-mark-supplier-btn" className="text-xs text-[#F5A623] hover:underline"
                      onClick={async () => {
                        try { await api.post(`/accounts/${id}/roles/supplier`, {}); toast.success("Als Lieferant markiert"); navigate(`/suppliers/${id}`); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                      }}>+ Als Lieferant markieren</button>
                  )
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 divide-x divide-white/10">
            <Kpi label={t("revenue_ytd")} value={kpis.revenue_ytd == null ? "—" : fmtEur(kpis.revenue_ytd)} accent />
            <Kpi label={t("open_opp_value")} value={kpis.open_opp_value == null ? "—" : fmtEur(kpis.open_opp_value)} />
            <Kpi label={t("open_complaints")} value={kpis.open_complaints} />
            <Kpi label={t("avg_margin")} value={kpis.avg_margin == null ? "—" : `${kpis.avg_margin}%`} />
            {canWrite && (
              <div className="pl-3">
                <Button size="sm" variant="secondary" onClick={() => setEditing(true)} data-testid="account-edit-btn">
                  <Pencil className="h-3.5 w-3.5 mr-1" />{t("edit")}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/60" data-testid="account-owners">
          <UsersIcon className="h-3.5 w-3.5" />{t("responsible_reps")}:
          {ownerIds.length ? ownerIds.map((oid) => (
            <Badge key={oid} className="bg-white/10 text-white hover:bg-white/10" data-testid={`account-owner-${oid}`}>{a.owner_name && ownerIds[0] === oid ? a.owner_name : nameOf(oid)}</Badge>
          )) : <span className="text-white/40">–</span>}
          {canReassign && (
            <Button size="sm" variant="secondary" className="h-6 px-2 text-xs ml-1" onClick={openOwners} data-testid="reassign-btn">{t("reassign")}</Button>
          )}
        </div>
        {a.parent_account_id && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-white/60">
            <GitBranch className="h-3.5 w-3.5" />{t("parent_account")}:
            <button className="text-[#F5A623] hover:underline" onClick={() => navigate(`/accounts/${a.parent_account_id}`)}>{a.parent_account_name}</button>
          </div>
        )}
      </div>

      <Tabs defaultValue="contacts">
        <TabsList className="bg-white border border-gray-200 flex-wrap h-auto">
          <TabsTrigger value="contacts" data-testid="account-tab-contacts">{t("contacts")} ({contacts.length})</TabsTrigger>
          <TabsTrigger value="org_chart" data-testid="account-tab-org-chart">{t("orgchart_tab")}</TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="account-tab-opportunities">{t("opportunities")} ({opportunities.length})</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="account-tab-quotes">{t("quotes")} ({quotes.length})</TabsTrigger>
          <TabsTrigger value="complaints" data-testid="account-tab-complaints">{t("complaints")} ({complaints.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="account-tab-orders">{t("orders")}</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="account-tab-contracts">{t("contracts")}</TabsTrigger>
          <TabsTrigger value="conditions" data-testid="account-tab-conditions">{t("order_conditions")}</TabsTrigger>
          <TabsTrigger value="articles" data-testid="account-tab-articles">{t("article_master")}</TabsTrigger>
          <TabsTrigger value="samples" data-testid="account-tab-samples">{t("samples")}</TabsTrigger>
          <TabsTrigger value="acts" data-testid="account-tab-activities">{t("activities")}</TabsTrigger>
          <TabsTrigger value="locations" data-testid="account-tab-locations">{t("locations")}</TabsTrigger>
          <TabsTrigger value="visit_notes" data-testid="account-tab-visit-notes">{t("visit_notes")}</TabsTrigger>
          {hasIntel && <TabsTrigger value="account_analysis" data-testid="account-tab-analysis">{t("account_analysis")}</TabsTrigger>}
          {hasIntel && <TabsTrigger value="wallet_share" data-testid="account-tab-wallet">{t("wallet_share_tab")}</TabsTrigger>}
          <TabsTrigger value="network" data-testid="account-tab-network">{t("network")}</TabsTrigger>
          <TabsTrigger value="performance" data-testid="account-tab-performance">{t("performance")}</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="account-tab-forecast">{t("customer_forecast")}</TabsTrigger>
          {isGroup && <TabsTrigger value="group" data-testid="account-tab-group">{t("group")}</TabsTrigger>}
          <TabsTrigger value="info" data-testid="account-tab-info">{t("info")}</TabsTrigger>
          <TabsTrigger value="activity" data-testid="account-tab-activity">{t("comments")}</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          {canWrite && (
            <div className="flex justify-end mb-2">
              <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white"
                data-testid="account-new-contact-btn" onClick={() => setNewContact(true)}>
                <UsersIcon className="h-4 w-4 mr-1.5" /> {t("new_contact")}
              </Button>
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {contacts.map((c) => (
              <div key={c.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40"
                onClick={() => openContactEdit(c.id)} data-testid="account-contact-row">
                <span className="font-medium min-w-[160px]">{c.first_name} {c.last_name}</span>
                <span className="text-gray-500">{c.title}</span>
                <Badge variant="outline">{c.buying_role}</Badge>
                <span className="text-gray-400">{c.department}</span>
                <span className="text-gray-500 ml-auto">{c.email}</span>
              </div>
            ))}
            {contacts.length === 0 && <p className="p-6 text-sm text-gray-400">{t("empty_title")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="org_chart">
          <OrgChart contacts={contacts} />
        </TabsContent>

        <TabsContent value="opportunities">
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {opportunities.map((o) => (
              <div key={o.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40" onClick={() => setOppDetail(o)} data-testid="account-opportunity-row">
                <span className="font-medium flex-1 min-w-[200px]">{o.name}</span>
                <Badge className={`${STAGE_COLORS[o.stage] || ""} hover:bg-inherit`}>{o.stage}</Badge>
                <span className="text-gray-400">{o.product_type}</span>
                <span className="font-mono">{fmtEur(o.amount)}</span>
              </div>
            ))}
            {opportunities.length === 0 && <p className="p-6 text-sm text-gray-400">{t("empty_title")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="quotes">
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {quotes.map((q) => (
              <div key={q.id} className="px-4 py-3 flex items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40" onClick={() => setQuoteDetail(q)} data-testid="account-quote-row">
                <span className="font-mono font-medium">{q.quote_number}</span>
                <Badge className={`${QUOTE_STATUS_COLORS[q.status] || ""} hover:bg-inherit`}>{q.status}</Badge>
                <span className="text-gray-400">v{q.version}</span>
                <span className="font-mono ml-auto">{fmtEur(q.total)}</span>
              </div>
            ))}
            {quotes.length === 0 && <p className="p-6 text-sm text-gray-400">{t("empty_title")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="complaints">
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {complaints.map((c) => (
              <div key={c.id} className="px-4 py-3 flex items-center gap-3 text-sm cursor-pointer hover:bg-amber-50/40" onClick={() => navigate(`/complaints/${c.id}`)}>
                <span className="font-mono font-medium">{c.complaint_number}</span>
                <Badge className={`${SEVERITY_COLORS[c.severity] || ""} hover:bg-inherit`}>{c.severity}</Badge>
                <span className="text-gray-500 flex-1 truncate">{plLabel("defect_categories", c.defect_category)}</span>
                <Badge variant="outline">{c.status}</Badge>
                <span className="font-mono">{fmtEur(c.claimed_value)}</span>
              </div>
            ))}
            {complaints.length === 0 && <p className="p-6 text-sm text-gray-400">{t("empty_title")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="orders"><AccountOrders accountId={id} /></TabsContent>
        <TabsContent value="contracts"><AccountContracts accountId={id} reloadToken={relatedToken} onEdit={(c) => setEdit({ kind: "contracts", data: c })} /></TabsContent>
        <TabsContent value="conditions"><AccountConditions accountId={id} reloadToken={relatedToken} onEdit={(c) => setEdit({ kind: "conditions", data: c })} /></TabsContent>
        <TabsContent value="articles"><AccountArticles accountId={id} reloadToken={relatedToken} onEdit={(a) => setEdit({ kind: "articles", data: a })} /></TabsContent>
        <TabsContent value="samples"><AccountSamplesArtworks accountId={id} reloadToken={relatedToken} onEdit={(kind, rec) => setEdit({ kind, data: rec })} /></TabsContent>
        <TabsContent value="acts"><AccountActivities accountId={id} /></TabsContent>
        <TabsContent value="locations"><AccountLocations accountId={id} reloadToken={relatedToken} onEdit={(l) => setEdit({ kind: "locations", data: l })} /></TabsContent>

        <TabsContent value="visit_notes">
          <VisitNotes accountId={id} />
        </TabsContent>

        {hasIntel && (
          <TabsContent value="account_analysis">
            <AccountAnalysis accountId={id} />
          </TabsContent>
        )}
        {hasIntel && (
          <TabsContent value="wallet_share">
            <WalletShare accountId={id} />
          </TabsContent>
        )}

        <TabsContent value="network">
          <AccountNetwork accountId={id} />
        </TabsContent>
        <TabsContent value="performance">
          <AccountKpis accountId={id} />
        </TabsContent>

        <TabsContent value="forecast">
          <ForecastPanel accountId={id} />
        </TabsContent>

        {isGroup && (
          <TabsContent value="group">
            <GroupRollup accountId={id} />
          </TabsContent>
        )}

        <TabsContent value="info">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-2 text-sm">
              {[["vat_id", a.vat_id], ["payment_terms", a.payment_terms], ["incoterm", a.incoterm],
                ["credit_limit", fmtEur(a.credit_limit)], ["billing_address", a.billing_address],
                ["customer_since", fmtDate(a.customer_since)], ["region", a.region], ["owner", a.owner_name]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-gray-50 py-1.5">
                  <span className="text-gray-500">{t(k)}</span><span className="font-medium text-right">{v || "–"}</span>
                </div>
              ))}
              <div className="pt-2">
                <span className="text-gray-500 text-xs uppercase tracking-widest">{t("shipping_addresses")}</span>
                {(a.shipping_addresses || []).map((s, i) => <p key={i} className="mt-1">{s}</p>)}
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" />{t("certifications")}</h3>
                <div className="flex flex-wrap gap-2">
                  {(a.certifications || []).map((c) => <Badge key={c} className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">{c}</Badge>)}
                  {(a.certifications || []).length === 0 && <span className="text-sm text-gray-400">–</span>}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><GitBranch className="h-4 w-4 text-[#1B2A4A]" />{t("hierarchy")}</h3>
                {a.parent_account_id && (
                  <button className="block text-sm text-blue-600 hover:underline mb-1" onClick={() => navigate(`/accounts/${a.parent_account_id}`)}>
                    ↑ {a.parent_account_name}
                  </button>
                )}
                <p className="text-sm font-medium pl-4 border-l-2 border-[#F5A623]">{a.name}</p>
                {children.map((c) => (
                  <button key={c.id} className="block text-sm text-blue-600 hover:underline pl-8 mt-1" onClick={() => navigate(`/accounts/${c.id}`)} data-testid="child-account-link">
                    └ {c.name}
                  </button>
                ))}
                {children.length === 0 && !a.parent_account_id && <p className="text-xs text-gray-400 mt-1">{t("children_accounts")}: –</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <CommentsAudit module="accounts" id={id} />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("edit")} – {a.name}</DialogTitle></DialogHeader>
          <EntityForm module="accounts" initial={a} onSaved={() => { setEditing(false); load(); }} onCancel={() => setEditing(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={newContact} onOpenChange={setNewContact}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("new_contact")} – {a.name}</DialogTitle></DialogHeader>
          <EntityForm module="contacts" initial={{ account_ids: [a.id], account_names: [a.name] }}
            confirmSave allowDelete
            onSaved={() => { setNewContact(false); load(); }} onCancel={() => setNewContact(false)} />
        </DialogContent>
      </Dialog>

      {oppDetail && (
        <OpportunityDetailDialog opportunity={oppDetail}
          onClose={() => setOppDetail(null)}
          onChanged={() => { setRelatedToken((x) => x + 1); load(); }} />
      )}
      {quoteDetail && (
        <QuoteDetailDialog quote={quoteDetail}
          onClose={() => setQuoteDetail(null)}
          onChanged={() => { setRelatedToken((x) => x + 1); load(); }} />
      )}
      {edit?.kind === "contracts" && (
        <ContractEditDialog contract={edit.data} onClose={() => closeEdit(false)} onSaved={() => closeEdit(true)} />
      )}
      {edit?.kind === "conditions" && (
        <ConditionEditDialog condition={edit.data} onClose={() => closeEdit(false)} onSaved={() => closeEdit(true)} />
      )}
      {edit?.kind === "articles" && (
        <ArticleEditDialog article={edit.data} onClose={() => closeEdit(false)} onSaved={() => closeEdit(true)} />
      )}
      {["contacts", "locations", "samples", "artworks"].includes(edit?.kind) && (
        <Dialog open onOpenChange={(o) => !o && closeEdit(false)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="account-record-edit-dialog">
            <DialogHeader><DialogTitle>{t("edit")} – {a.name}</DialogTitle>
              <DialogDescription>{a.account_number}</DialogDescription>
            </DialogHeader>
            <EntityForm module={edit.kind} initial={edit.data}
              {...(edit.kind === "contacts" ? { confirmSave: true, allowDelete: true, onDeleted: () => closeEdit(true) } : {})}
              onSaved={() => closeEdit(true)} onCancel={() => closeEdit(false)} />
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={ownersOpen} onOpenChange={setOwnersOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("responsible_reps")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-gray-100 rounded-md max-h-56 overflow-y-auto divide-y divide-gray-50">
              {users.map((u) => (
                <label key={u.id} className="px-3 py-2 flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50" data-testid={`owner-opt-${u.email}`}>
                  <Checkbox checked={ownerDraft.includes(u.id)} onCheckedChange={() => toggleOwner(u.id)} />
                  <span className="font-medium">{u.name}</span><span className="text-xs text-gray-400 ml-auto">{u.role}</span>
                </label>
              ))}
            </div>
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={saveOwners} data-testid="owners-save-btn">{t("save")}</Button>
            {history.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-1.5">{t("assignment_history")}</p>
                <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="assignment-history">
                  {history.map((h) => (
                    <div key={h.id} className="text-[11px] text-gray-500 flex items-center gap-2">
                      <span className="font-mono text-gray-400">{(h.changed_at || "").slice(0, 10)}</span>
                      <span>{(h.new_owner_ids || []).map(nameOf).join(", ") || "–"}</span>
                      <span className="text-gray-300 ml-auto">{h.changed_by}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
